/**
 * Help URL helpers — the single source of truth for how help links are built,
 * so the whole help section can flip from "served under the app at
 * app.mucka.ai/help/*" to "served on its own public subdomain
 * help.mucka.ai/*" by toggling ONE env flag.
 *
 * The public help site lives on `help.mucka.ai`, served by the SAME Next.js
 * app via hostname-based middleware rewriting (see src/lib/supabase/
 * middleware.ts). Internally every help route still lives under `/help/*`;
 * the subdomain just exposes clean, prefix-less URLs
 * (help.mucka.ai/quotes/send-your-first-quote → internal /help/quotes/...).
 *
 * NEXT_PUBLIC_HELP_SUBDOMAIN_LIVE:
 *   unset / "0" → pre-migration. Help is reachable at app.mucka.ai/help/*.
 *                 Internal <Link>s keep the /help prefix; the whole app
 *                 (help included) is noindexed. Safe default — merging this
 *                 code changes nothing user-visible until the flag flips.
 *   "1"        → migrated. help.mucka.ai is live (DNS + Vercel domain done).
 *                 Internal <Link>s go prefix-less (the middleware rewrite maps
 *                 them back to /help/*), app.mucka.ai/help/* 301s to the
 *                 subdomain, and help.mucka.ai is the one indexable surface.
 *
 * Set to "1" in Vercel Production only AFTER the subdomain resolves.
 */
export const HELP_SUBDOMAIN_LIVE =
  process.env.NEXT_PUBLIC_HELP_SUBDOMAIN_LIVE === '1';

/** Absolute origin of the public help site. Used for canonicals + sitemap
 *  (always help.mucka.ai — that's the canonical public home regardless of
 *  which host currently serves the bytes). */
export const HELP_ORIGIN = 'https://help.mucka.ai';

/**
 * The public "get a human" page on the marketing site — contact form plus the
 * support email. Help articles link out to it so a reader who hits the end of
 * an article has somewhere to go, and so mucka.ai and help.mucka.ai link to
 * each other rather than the help centre sitting on the web as an orphan.
 */
export const SUPPORT_URL = 'https://mucka.ai/support/';

/** Absolute origin of the authed app — help articles deep-link into it
 *  ("head to Settings → Team"). On the subdomain those links must be
 *  absolute or they'd resolve to help.mucka.ai and 404. */
export const APP_ORIGIN = 'https://app.mucka.ai';

/**
 * Root-relative href for an internal help route, for use in <Link>/<a> WITHIN
 * the help section. Pass the CLEAN path (no /help prefix), e.g. '/quotes' or
 * '/quotes/send-your-first-quote' or '/' for the landing page.
 *
 * - Migrated  → returns the clean path unchanged (subdomain root).
 * - Pre-migration → prefixes '/help' so it resolves under the app.
 */
export function helpPath(clean: string): string {
  const c = clean === '/' ? '' : clean;
  return HELP_SUBDOMAIN_LIVE ? c || '/' : `/help${c}`;
}

/**
 * Public URL for a help article/route as seen OUTSIDE the help section —
 * the "Open full article" link in the in-app help sheet, or anywhere the
 * app points a user at the public docs. Pass the CLEAN path.
 *
 * - Migrated  → absolute https://help.mucka.ai/... (opens the public site).
 * - Pre-migration → root-relative /help/... (stays inside the app).
 */
export function helpPublicUrl(clean: string): string {
  const c = clean === '/' ? '' : clean;
  return HELP_SUBDOMAIN_LIVE ? `${HELP_ORIGIN}${c}` : `/help${c}`;
}

/**
 * Root-relative link INTO the authed app (`/jobs`, `/settings?tab=team`, `/`).
 * On the public subdomain these must be absolute app.mucka.ai URLs or they'd
 * resolve to help.mucka.ai and 404/rewrite wrongly. Anchors, absolute URLs and
 * mailto/tel are left untouched.
 *
 * - Migrated  → absolute https://app.mucka.ai/...
 * - Pre-migration → unchanged (help is served under the app, same origin).
 */
export function appLink(href: string): string {
  if (!HELP_SUBDOMAIN_LIVE) return href;
  if (!href.startsWith('/') || href.startsWith('//')) return href; // #anchor, http(s), mailto:, tel:
  return `${APP_ORIGIN}${href}`;
}

/**
 * Resolve a link found inside help-article MDX for the environment it's served
 * in. Two kinds of link appear in article bodies:
 *   - help cross-links, written `/help/<category>/<slug>` (→ another article)
 *   - app deep-links, written `/settings`, `/jobs`, ... (→ "head to X")
 * On the subdomain the first must become a clean help path and the second an
 * absolute app.mucka.ai URL. Everything else (http, #anchor, mailto) passes
 * through untouched.
 */
export function resolveHelpMdxHref(href: string | undefined): string | undefined {
  if (!href) return href;
  if (href === '/help' || href.startsWith('/help/')) {
    return helpPath(href.slice('/help'.length) || '/');
  }
  if (href.startsWith('/') && !href.startsWith('//')) {
    return appLink(href);
  }
  return href;
}
